import {Component, Inject, inject, OnDestroy, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {Subscription} from 'rxjs';
import {
  AppointmentNotification,
  AppointmentNotificationTypeLabels,
  AppointmentNotificationStatusLabels,
  AppointmentNotificationType,
  AppointmentNotificationStatus,
  AppointmentNotificationChannel,
  PracticeDateTimePipe
} from '@xmedical/core';
import {MatTableModule} from '@angular/material/table';
import {MatButtonModule} from '@angular/material/button';
import {XmedicalUiModule} from '@xmedical/ui';
import {CALENDAR_DATA_PROVIDER} from '../../tokens/calendar.tokens';

export interface AppointmentNotificationLogDialogData {
  appointmentId: number;
}

@Component({
  selector: 'app-appointment-notification-log',
  templateUrl: './appointment-notification-log.component.html',
  styleUrls: ['./appointment-notification-log.component.scss'],
  imports: [
    PracticeDateTimePipe,
    MatDialogModule,
    MatTableModule,
    MatButtonModule,
    XmedicalUiModule,
  ]
})
export class AppointmentNotificationLogComponent implements OnInit, OnDestroy {

  notifications: AppointmentNotification[] = [];
  loading = true;
  displayedColumns = ['notificationType', 'channel', 'status', 'recipient', 'scheduledSendTime', 'sentTime', 'subject', 'result'];

  private subscription: Subscription;
  private dataProvider = inject(CALENDAR_DATA_PROVIDER);

  constructor(
    public dialogRef: MatDialogRef<AppointmentNotificationLogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AppointmentNotificationLogDialogData,
  ) {}

  ngOnInit(): void {
    // The notifications endpoint is practice-scoped; only providers that support it
    // (xmedical-app) implement loadAppointmentNotifications. The entry points are
    // hidden when it's absent, but guard here too so the dialog degrades gracefully.
    const load = this.dataProvider.loadAppointmentNotifications;
    if (!load) {
      this.loading = false;
      return;
    }
    this.subscription = load.call(this.dataProvider, this.data.appointmentId).subscribe({
      next: (notifications) => {
        this.notifications = notifications;
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading notification log', error);
        this.loading = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  getTypeLabel(type: AppointmentNotificationType): string {
    return AppointmentNotificationTypeLabels[type] ?? 'Unknown';
  }

  getStatusLabel(status: AppointmentNotificationStatus): string {
    return AppointmentNotificationStatusLabels[status] ?? 'Unknown';
  }

  getChannelLabel(channel: AppointmentNotificationChannel): string {
    if (channel === AppointmentNotificationChannel.Sms) return 'SMS';
    if (channel === AppointmentNotificationChannel.Email) return 'Email';
    return 'Unknown';
  }

  /** The recipient is the email for an email notification and the phone for an SMS;
   *  show whichever is populated so SMS rows don't render a blank recipient. */
  getRecipient(notification: AppointmentNotification): string {
    return notification.recipientEmail || notification.recipientPhone || '';
  }

  close() {
    this.dialogRef.close();
  }
}
